import axios, { type AxiosInstance, type AxiosRequestConfig } from 'axios'
import { getEnv } from '@/env'
import { useAppStore } from '@/store/appStore'

// ─── Instance ─────────────────────────────────────────────────────────────────

const KSEC_BASE_URL =
  getEnv('IT_MFE_KSEC_BASE_URL') || 'http://localhost:3000/ksec/v2'

const ksecHttp: AxiosInstance = axios.create({
  baseURL: KSEC_BASE_URL,
  headers: { 'Content-Type': 'application/json' },
  timeout: 15_000,
})

// ─── Request interceptor ──────────────────────────────────────────────────────

ksecHttp.interceptors.request.use((config) => {
  const { auth, identity } = useAppStore.getState()

  // Auth0 access_token (audience = KSEC API). No usar id_token aquí.
  if (auth.jwt && !config.headers.Authorization) {
    config.headers.Authorization = `Bearer ${auth.jwt}`
  }

  if (identity.partyId && !config.headers['x-kbrm-party-id']) {
    config.headers['x-kbrm-party-id'] = identity.partyId
  }

  return config
})

// ─── Response interceptor ─────────────────────────────────────────────────────

ksecHttp.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      useAppStore.getState().reset()
      window.location.href = '/'
    }
    return Promise.reject(error)
  },
)

// ─── Helpers ──────────────────────────────────────────────────────────────────

export async function ksecGet<T>(url: string, config?: AxiosRequestConfig): Promise<T> {
  const { data } = await ksecHttp.get<T>(url, config)
  return data
}

export async function ksecPost<T>(
  url: string,
  body?: unknown,
  config?: AxiosRequestConfig,
): Promise<T> {
  const { data } = await ksecHttp.post<T>(url, body, config)
  return data
}

export async function ksecPatch<T>(
  url: string,
  body?: unknown,
  config?: AxiosRequestConfig,
): Promise<T> {
  const { data } = await ksecHttp.patch<T>(url, body, config)
  return data
}

export async function ksecDelete<T>(url: string, config?: AxiosRequestConfig): Promise<T> {
  const { data } = await ksecHttp.delete<T>(url, config)
  return data
}

export default ksecHttp
